import React, { useState, useEffect } from 'react'
import { Modal, FormGroup, FormControl, Button, Radio } from 'react-bootstrap'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import ReactSelect, { Option } from 'react-select'

import Label from 'live/components/forms/ui/label'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { AbsoluteLoading, RoundedModal } from 'plugin-channelmanager/generic'
import { updateChannel } from 'plugin-channelmanager/services'
import { listChannelTypes } from 'plugin-channelmanager/services/channelTypes'

import { AddChannelModalBody } from './styles'

interface IProps {
    show: boolean
    listData: IListData
    handleClose: () => void
    handleSave: () => void
}

interface IListData {
    id: string
    name: string
    channelType: string
}

interface IChannelCreationForm extends Omit<IChannel, 'channelType'> {
    idType: 'manual' | 'automatic'
    channelType: Option<number>
}

const formDefault: IChannelCreationForm = {
    name: '',
    id: '',
    channelType: { value: null, label: '' },
    idType: 'automatic'
}

export const EditChannelModal: React.FC<IProps> = ({ show, listData, handleClose, handleSave }) => {
    const { handleSubmit, control, formState, setValue, watch, reset } = useForm<IChannelCreationForm>({
        mode: 'all',
        defaultValues: formDefault
    })

    const [channelTypes, setChannelTypes] = useState<Option<string>>([])
    const [isLoading, setIsLoading] = useState(true)

    const [channelName, setChannelName] = useState('')
    const [channelType, setChannelType] = useState({ value: null, label: '' })

    const handleCloseProtocol = (fromCreation?: boolean): void => {
        reset()

        if (fromCreation) {
            handleSave()
        } else {
            handleClose()
        }
    }

    const handleUpdate = async (): Promise<void> => {
        setIsLoading(true)
        try {
            const channel: IChannelCreation = {
                id: listData.id,
                name: channelName,
                channelTypeId: channelType.value
            }
            const postData = await updateChannel(channel)

            handleCloseProtocol(true)
            globalNotification.success(i18n('Channel updated successfully.'))
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on updating channel.'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleListChannelTypes = async (): Promise<void> => {
        setIsLoading(true)
        try {
            const response = await listChannelTypes()

            setChannelTypes(response.map(op => ({ value: op.id, label: i18n(op.name) })))
            setIsLoading(false)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error ocurred on listing channel types'))

            return handleListChannelTypes()
        }
    }

    const handleSetFormValues = (channelData?: IListData): void => {
        if (channelData == null) {
            return
        }
        setChannelName(channelData.name)
        const selectedType = channelTypes.find(type => {
            return type.value.toLowerCase() === channelData.channelType.toLowerCase()
        })
        setChannelType(selectedType)
    }

    const isFormInvalid = (): boolean => {
        return !listData?.id || !channelName || channelName?.length === 0 || !channelType
    }

    useEffect(() => {
        handleListChannelTypes()
        handleSetFormValues(listData)
    }, [listData])

    return (
        <RoundedModal show={show} onHide={() => handleCloseProtocol()} aria-labelledby="contained-modal-title">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title">{i18n('Edit Channel')}</Modal.Title>
            </Modal.Header>
            <AddChannelModalBody>
                <Controller
                    rules={{ required: true }}
                    name="name"
                    control={control}
                    render={() => (
                        <FormGroup validationState={!channelName ? 'error' : 'success'}>
                            <Label required>{i18n('Channel Name')}</Label>
                            <FormControl
                                type="text"
                                id="channelsList-editModal-channel-name"
                                onChange={e => {
                                    setChannelName((e.target as HTMLInputElement).value)
                                }}
                                value={channelName}
                                maxLength={255}
                            />
                        </FormGroup>
                    )}
                />

                <Controller
                    rules={{ required: true }}
                    name="channelType"
                    control={control}
                    render={() => (
                        <FormGroup validationState={!channelType ? 'error' : 'success'}>
                            <Label required>{i18n('Type')}</Label>
                            <ReactSelect
                                options={channelTypes}
                                id="channelsList-editModal-channel-type"
                                onChange={(obj: Option<number>) =>
                                    setChannelType({ value: obj.value, label: obj.label })
                                }
                                value={channelType}
                                searchable={true}
                                clearable={false}
                            />
                        </FormGroup>
                    )}
                />

                <Controller
                    rules={{ required: true }}
                    name="id"
                    control={control}
                    render={() => (
                        <FormGroup validationState={!listData.id ? 'error' : 'success'}>
                            <Label required>{i18n('ID')}</Label>
                            <FormControl
                                disabled={true}
                                type="text"
                                id="channelsList-editModal-channel-id"
                                value={listData.id}
                                // disabled={idType === 'automatic'}
                            />
                        </FormGroup>
                    )}
                />
            </AddChannelModalBody>
            <Modal.Footer>
                <Button
                    className="no-outline-button"
                    bsStyle="primary"
                    type="submit"
                    id="diagramList-editModal-button-update"
                    disabled={isFormInvalid()}
                    onClick={handleUpdate}
                >
                    {i18n('Save')}
                </Button>
                <Button
                    className="no-outline-button"
                    type="button"
                    id="diagramList-editModal-button-close"
                    onClick={() => handleCloseProtocol()}
                >
                    {i18n('Close')}
                </Button>
            </Modal.Footer>
            <AbsoluteLoading show={isLoading} />
        </RoundedModal>
    )
}
